import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CannedMessagePicker.css';

const CannedMessagePicker = ({ onSelect }) => {
    const [cannedMessages, setCannedMessages] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            setLoading(true);
            // Fetch only active messages with active categories
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/cannedmessages?forPicker=true`,
                { withCredentials: true }
            );
            setCannedMessages(response.data);
        } catch (error) {
            console.error('Error fetching canned messages:', error);
        } finally {
            setLoading(false);
        }
    };

    // Group messages by category
    const groupedMessages = cannedMessages.reduce((acc, msg) => {
        if (msg.category) {
            const catId = msg.category._id;
            if (!acc[catId]) {
                acc[catId] = {
                    category: msg.category,
                    messages: []
                };
            }
            acc[catId].messages.push(msg);
        }
        return acc;
    }, {});

    // Filter messages by search term
    const filteredGroups = Object.keys(groupedMessages).reduce((acc, catId) => {
        const { category, messages } = groupedMessages[catId];
        const filtered = messages.filter(msg =>
            msg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filtered.length > 0) {
            acc[catId] = { category, messages: filtered };
        }
        return acc;
    }, {});

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const handleSelect = (message) => {
        onSelect(message.description);
    };

    if (loading) {
        return (
            <div className="canned-picker-dropdown">
                <div className="canned-picker-content">
                    <div className="canned-empty">Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="canned-picker-dropdown">
            <div className="canned-picker-header">
                <input
                    type="text"
                    placeholder="Search canned messages..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="canned-search-input"
                />
            </div>

            <div className="canned-picker-content">
                {Object.keys(filteredGroups).length === 0 ? (
                    <div className="canned-empty">
                        {searchTerm ? 'No messages found' : 'No active messages available'}
                    </div>
                ) : (
                    Object.keys(filteredGroups).sort((a, b) => {
                        const catA = filteredGroups[a].category.title;
                        const catB = filteredGroups[b].category.title;
                        return catA.localeCompare(catB);
                    }).map(catId => {
                        const { category, messages } = filteredGroups[catId];
                        return (
                            <div key={catId} className="canned-category-group">
                                <div
                                    className="canned-category-header"
                                    onClick={() => toggleCategory(catId)}
                                    style={{ borderLeftColor: category.color }}
                                >
                                    <span className="category-name">{category.title}</span>
                                    <span className="category-count">({messages.length})</span>
                                    <span className={`category-arrow ${expandedCategories[catId] ? 'expanded' : ''}`}>
                                        ▼
                                    </span>
                                </div>

                                {expandedCategories[catId] && (
                                    <div className="canned-messages-list">
                                        {messages.map(msg => (
                                            <div
                                                key={msg._id}
                                                className="canned-message-item"
                                                onClick={() => handleSelect(msg)}
                                            >
                                                <div className="canned-msg-title">{msg.title}</div>
                                                <div className="canned-msg-preview">
                                                    {msg.description.substring(0, 60)}
                                                    {msg.description.length > 60 ? '...' : ''}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default CannedMessagePicker;
